import React, { memo, useEffect, useRef } from 'react';
import { Terminal } from 'lucide-react';
import { ExecutionStep } from '../../../core/types/rust-engine-interfaces';

interface ExecutionLogProps {
  steps: ExecutionStep[];
  currentStepIndex: number;
}

const CHIP_MAP: Record<string, { bg: string; text: string; label: string }> = {
  comparison:    { bg: 'rgba(245,158,11,0.11)', text: '#d97706', label: 'CMP'  },
  swap:          { bg: 'rgba(239,68,68,0.11)',  text: '#ef4444', label: 'SWAP' },
  update:        { bg: 'rgba(59,130,246,0.11)', text: '#60a5fa', label: 'UPD'  },
  state_change:  { bg: 'rgba(168,85,247,0.11)', text: '#a855f7', label: 'ROT'  },
  visit:         { bg: 'rgba(16,185,129,0.11)', text: '#10b981', label: 'VISIT'},
  highlight:     { bg: 'rgba(139,92,246,0.11)', text: '#8b5cf6', label: 'HL'   },
  initialization:{ bg: 'rgba(6,182,212,0.11)',  text: '#22d3ee', label: 'INIT' },
  decision:      { bg: 'rgba(251,191,36,0.11)', text: '#fbbf24', label: 'DEC'  },
  error:         { bg: 'rgba(239,68,68,0.16)',  text: '#fca5a5', label: 'ERR'  },
  completion:    { bg: 'rgba(16,185,129,0.13)', text: '#34d399', label: 'DONE' },
};

function StepChip({ stepType }: { stepType: string | undefined }) {
  if (!stepType) return null;
  const cfg = CHIP_MAP[stepType.toLowerCase()];
  if (!cfg) return null;
  return (
    <span
      className="flex-shrink-0 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded leading-none"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      {cfg.label}
    </span>
  );
}

export const ExecutionLog: React.FC<ExecutionLogProps> = memo(({ steps, currentStepIndex }) => {
  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentStepIndex]);

  return (
    <div
      className="flex-shrink-0 flex flex-col"
      style={{
        height: 'clamp(128px, 20vh, 220px)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center gap-2 px-3 py-1.5 flex-shrink-0"
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <Terminal className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#22c55e' }} />
        <span className="text-[10px] font-semibold tracking-[0.1em] uppercase" style={{ color: '#52525b' }}>
          Execution Log
        </span>
        {steps.length > 0 && (
          <span className="ml-auto text-[10px] font-mono tabular-nums" style={{ color: '#3f3f46' }}>
            {currentStepIndex + 1} / {steps.length}
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto">
        {steps.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-[10px] font-mono" style={{ color: '#3f3f46' }}>
              — awaiting execution —
            </span>
          </div>
        ) : (
          <div className="p-1.5 space-y-0.5">
            {steps.map((step, idx) => {
              const isActive = idx === currentStepIndex;
              const stableKey = step.stepId != null ? step.stepId : idx;
              return (
                <div
                  key={stableKey}
                  ref={isActive ? activeRef : null}
                  className="flex items-start gap-2 px-2 py-1 rounded-md transition-colors duration-150"
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.03)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.background = isActive ? 'rgba(59,130,246,0.07)' : 'transparent';
                  }}
                  style={isActive ? {
                    background: 'rgba(59,130,246,0.07)',
                    border: '1px solid rgba(59,130,246,0.16)',
                  } : undefined}
                >
                  <span
                    className="text-[9px] font-mono tabular-nums flex-shrink-0 mt-0.5"
                    style={{ color: isActive ? '#60a5fa' : '#3f3f46' }}
                  >
                    {String(idx + 1).padStart(3, '0')}
                  </span>
                  <StepChip stepType={step.stepType as string | undefined} />
                  <span className="text-[11px] leading-[1.4] min-w-0 break-words" style={{ color: isActive ? '#e4e4e7' : '#52525b' }}>
                    {step.metadata.explanation || 'Step completed'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});
